import React, { useEffect } from "react";
import RSelect from 'react-select'
import { useSelector } from "react-redux";
import {
    Typography,
    IconButton,
    Chip,
    Card,
    Button,
    Select,
} from "@material-tailwind/react";
import { Xmark, User } from "iconoir-react";
import { Task, TaskBlocker } from "../redux/tasks/actions";
import { Part } from "../redux/vehicles/actions";
import AppImages from "../assets/AppImages";
import { DatepickerInput } from "./DatapickerInput";
import { Employee } from "../redux/employee/actions";
import { RootState } from "../redux";
import { getTaskStatus, getTaskStatusColor } from "../utils/formatViews";

interface TaskDetailsSidePanelProps {
    tasks: Task[];
    selectedTask: Task;
    onClose: (task: Task) => void;
}

export const TaskDetailsSidePanel: React.FC<TaskDetailsSidePanelProps> = ({
    tasks,
    selectedTask,
    onClose,
}) => {
    const employees = useSelector((state: RootState) => state.employees.employees);
    const [blockers, setBlockers] = React.useState<TaskBlocker[]>([]);
    const [task, setTask] = React.useState<Task>(selectedTask);
    const [usedTasks, setUsedTasks] = React.useState<number[]>([]);
    const [usedParts, setUsedParts] = React.useState<number[]>([]);
    const { parts_tasks } = useSelector((state: RootState) => state.parts);

    useEffect(() => {
        const dependencies = blockers
            ?.filter((blocker: TaskBlocker) => blocker.type === "blocker")
            .map((blocker: TaskBlocker) => blocker.task);
        const blocked_by = blockers
            ?.filter((blocker: TaskBlocker) => blocker.type === "blocked")
            .map((blocker: TaskBlocker) => blocker.task);
        setTask((task: Task) => ({ ...task, dependencies: dependencies, blocked_by: blocked_by }));
        setUsedTasks(blockers.map((blocker: TaskBlocker) => blocker.task.id as number));
    }, [blockers]);

    useEffect(() => {
        setTask(selectedTask);

        const blockers: TaskBlocker[] = [];
        let id = 0;

        if (selectedTask?.blocked_by?.length) {
            blockers.push(
                ...selectedTask.blocked_by.map(
                    (task: Task) => ({ type: "blocked", task: task, id: id++ } as TaskBlocker)
                )
            );
        }
        if (selectedTask?.dependencies?.length) {
            blockers.push(
                ...selectedTask.dependencies.map(
                    (task: Task) => ({ type: "blocker", task: task, id: id++ } as TaskBlocker)
                )
            );
        }

        setBlockers(blockers);
    }, [selectedTask]);

    useEffect(() => {
        setUsedParts(task?.parts?.map((part: Part) => part.id || 0) || []);
    }, [task]);

    return (
        <Card className="fixed w-[calc(min(600px,95vw))] bottom-0 right-0 z-20 top-[145px] !shadow-xl text-primary rounded-none overflow-y-auto scrollbar-hide">
            {/* Header */}
            <Card.Header className="rounded-none bg-white p-3 border-b border-blue-gray-100">
                <div className="flex items-center justify-between mb-2">
                    <Typography variant="h5" className="text-primary font-bold w-3/4">
                        {task?.name}
                    </Typography>
                    <div className="flex gap-x-2">
                        <Button
                            variant="outline"
                            color="secondary"
                            className={`px-4 py-2 text-nowrap rounded-md ${
                                task?.is_complete ? "bg-success text-white font-blacK" : ""
                            }`}
                            onClick={() => {
                                setTask({ ...task, is_complete: !task.is_complete });
                            }}
                        >
                            {task?.is_complete ? "Completed" : "Mark as done"}
                        </Button>
                        <IconButton size="sm" variant="ghost" onClick={() => onClose(task)}>
                            <Xmark className="h-5 w-5" />
                        </IconButton>
                    </div>
                </div>
            </Card.Header>

            {/* Content */}
            <Card.Body className="overflow-y-auto p-5 space-y-6">
                <div className="flex flex-col gap-4">
                    <div className="flex items-center">
                        <label className="w-1/3 pr-4">Status</label>
                        <div className="w-2/3 border-secondary text-sm rounded">
                            <Chip isPill={false} color={getTaskStatusColor(task)}>
                                <Chip.Label variant="small">{getTaskStatus(task)}</Chip.Label>
                            </Chip>
                        </div>
                    </div>
                    <div className="flex items-center">
                        <label className="w-1/3 pr-4">Name</label>
                        <input
                            onChange={(e) => {
                                setTask((task: Task) => ({ ...task, name: e.target.value }));
                            }}
                            value={task?.name}
                            type="text"
                            placeholder="Task Name"
                            className="w-2/3 p-2 border-secondary text-sm rounded"
                        />
                    </div>
                    <div className="flex items-center">
                        <label className="w-1/3 pr-4">Assignee</label>
                        <RSelect
                            value={employees.find((emp) => emp.id === task?.assignee_id)}
                            onChange={(selected) => {
                                setTask((task: Task) => ({
                                    ...task,
                                    assignee_id: selected?.id || null,
                                }));
                            }}
                            options={employees}
                            getOptionLabel={(option: Employee) =>
                                option.user?.first_name || option.user?.last_name
                                    ? [option.user?.first_name, option.user?.last_name].join(" ")
                                    : option.user?.email || ""
                            }
                            getOptionValue={(option: Employee) => option.id?.toString() || ""}
                            formatOptionLabel={(option: Employee) => (
                                <div className="flex items-center gap-2">
                                    {option?.avatar ? (
                                        <div className="flex items-center justify-center w-7 h-7 bg-gray-200 rounded-full">
                                            <img
                                                src={option?.avatar}
                                                alt="avatar"
                                                className="h-5 w-5 rounded-full"
                                            />
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-center w-7 h-7 bg-gray-200 rounded-full">
                                            <User className="h-5 w-5" />
                                        </div>
                                    )}
                                    <span className="text-sm">
                                        {option.user?.first_name || option.user?.last_name
                                            ? [
                                                  option.user?.first_name,
                                                  option.user?.last_name,
                                              ].join(" ")
                                            : option.user?.email}
                                    </span>
                                </div>
                            )}
                            className="w-2/3"
                            classNames={{
                                control: () => "p-1 border-secondary rounded",
                                option: () =>
                                    "p-1 hover:bg-gray-100 odd:bg-gray-100 cursor-pointer",
                            }}
                        />
                    </div>
                    <div className="flex items-center">
                        <label className="w-1/3 pr-4">Date</label>
                        <DatepickerInput
                            date={task?.due_date ? new Date(task.due_date + 'T00:00:00') : null}
                            setDate={(date: Date) => {
                                setTask((task: Task) => ({
                                    ...task,
                                    due_date: date.toISOString().split("T")[0],
                                }));
                            }}
                        />
                    </div>
                    <div className="flex items-center">
                        <label className="w-1/3 pr-4">Hours</label>
                        <input
                            type="text"
                            onChange={(e) => {
                                setTask((task: Task) => ({ ...task, hours: e.target.value }));
                            }}
                            placeholder="Est. hours"
                            value={task?.hours}
                            className="w-2/3 p-2 border-secondary text-sm rounded"
                        />
                    </div>

                    <div className="flex items-top mt-3">
                        <label className="w-1/3 pr-4 flex items-center">
                            Dependencies
                        </label>
                        <div className="w-2/3 flex flex-col gap-3 items-center">
                            {blockers.map((blocker: TaskBlocker) => (
                                <div key={blocker.id} className="flex w-full items-top gap-3">
                                    <div className="min-w-max flex items-center gap-1">
                                        <Select
                                            value={blocker.type}
                                            onValueChange={(val) => {
                                                setBlockers((prevBlockers: TaskBlocker[]) =>
                                                    prevBlockers.map((el) =>
                                                        el.id === blocker.id
                                                            ? { ...el, type: val }
                                                            : el
                                                    )
                                                );
                                            }}
                                        >
                                            <Select.Trigger
                                                className="p-2 w-full border-secondary text-sm rounded text-ellipsis overflow-hidden"
                                                placeholder="Select type"
                                            />
                                            <Select.List className="w-[200px]">
                                                <Select.Option
                                                    value="blocked"
                                                    className="text-left odd:bg-gray-100"
                                                >
                                                    <Typography
                                                        variant="small"
                                                        className="text-warning text-sm w-[70px]"
                                                    >
                                                        Blocked by
                                                    </Typography>
                                                </Select.Option>
                                                <Select.Option
                                                    value={"blocker"}
                                                    className="text-left odd:bg-gray-100"
                                                >
                                                    <Typography
                                                        variant="small"
                                                        className="text-error text-sm w-[70px]"
                                                    >
                                                        Blocker
                                                    </Typography>
                                                </Select.Option>
                                            </Select.List>
                                        </Select>
                                    </div>
                                    <div className="flex-grow">
                                        <Select
                                            value={blocker.task.id?.toString()}
                                            onValueChange={(val) => {
                                                setBlockers((prevBlockers: TaskBlocker[]) =>
                                                    prevBlockers.map((el) =>
                                                        el.id === blocker.id
                                                            ? ({
                                                                  ...el,
                                                                  task: tasks.find(
                                                                      (task) =>
                                                                          task.id === Number(val)
                                                                  ),
                                                              } as TaskBlocker)
                                                            : el
                                                    )
                                                );
                                            }}
                                        >
                                            <Select.Trigger
                                                className={`${
                                                    blocker.task.name
                                                        ? "!ring-0 !border-0 !shadow-none"
                                                        : ""
                                                } p-2 w-full text-sm rounded text-ellipsis overflow-hidden`}
                                                placeholder="Find a task..."
                                            />
                                            <Select.List className="w-[200px]">
                                                {tasks
                                                    .filter((t: Task) => t.id != task.id)
                                                    .filter(
                                                        (t: Task) =>
                                                            !usedTasks.includes(t.id as number)
                                                    )
                                                    .map((task: Task, idx: number) => (
                                                        <Select.Option
                                                            key={idx}
                                                            value={task.id?.toString()}
                                                            className="text-left bg-white odd:bg-gray-100"
                                                        >
                                                            {task.name}
                                                        </Select.Option>
                                                    ))}
                                            </Select.List>
                                        </Select>
                                    </div>
                                    <IconButton
                                        variant="ghost"
                                        onClick={() => {
                                            setBlockers((blockers: TaskBlocker[]) =>
                                                blockers.filter(
                                                    (el) => el.task.id !== blocker.task.id
                                                )
                                            );
                                        }}
                                    >
                                        <AppImages.iconTrash className="text-error w-5 h-5" />
                                    </IconButton>
                                </div>
                            ))}
                            <div className="flex w-full justify-start">
                                <Button
                                    size="sm"
                                    color="primary"
                                    className="!align-start"
                                    onClick={() => {
                                        setBlockers((blockers: TaskBlocker[]) => [
                                            ...blockers,
                                            {
                                                type: "blocker",
                                                task: { name: "" },
                                                id: (blockers[blockers.length - 1]?.id || 0) + 1,
                                            },
                                        ]);
                                    }}
                                >
                                    Add Task
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-top mt-3">
                        <label className="w-1/3 pr-4 flex items-center">Parts</label>
                        <div className="w-2/3 flex flex-col gap-3 items-center">
                            {task?.parts?.map((part: Part, index: number) => (
                                <div key={part.id} className="flex w-full items-top gap-3">
                                    <div className="flex-grow">
                                        <RSelect
                                            value={parts_tasks.find((p) => p.id === part.id)}
                                            onChange={(selected) => {
                                                setTask((task: Task) => ({
                                                    ...task,
                                                    parts: task?.parts?.map((p, i) =>
                                                        i === index ? selected : p
                                                    ),
                                                }));
                                            }}
                                            options={parts_tasks.filter(
                                                (part: Part) => !usedParts.includes(part.id || 0)
                                            )}
                                            getOptionLabel={(option: Part) => option.name || ""}
                                            getOptionValue={(option: Part) =>
                                                option.id?.toString() || ""
                                            }
                                            formatOptionLabel={(option: Part) => (
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm">{option.name}</span>
                                                </div>
                                            )}
                                            className="w-full"
                                            classNames={{
                                                control: () => "p-1 border-secondary rounded",
                                                option: () => "p-1 hover:bg-gray-100 cursor-pointer",
                                            }}
                                            placeholder="Find a part..."
                                        />
                                    </div>

                                    <IconButton
                                        variant="ghost"
                                        onClick={() => {
                                            setBlockers((blockers: TaskBlocker[]) =>
                                                blockers.filter((el) => el.task.id !== part.id)
                                            );
                                        }}
                                    >
                                        <AppImages.iconTrash className="text-error w-5 h-5" />
                                    </IconButton>
                                </div>
                            ))}
                            <div className="flex w-full justify-start">
                                <Button
                                    size="sm"
                                    color="primary"
                                    className="!align-start"
                                    onClick={() => {
                                        if (
                                            task?.parts &&
                                            task?.parts[task?.parts?.length - 1]?.id != 0
                                        ) {
                                            setTask((task: Task) => ({
                                                ...task,
                                                parts: [
                                                    ...(task?.parts || []),
                                                    {
                                                        id: 0,
                                                        name: "",
                                                    } as unknown as Part,
                                                ],
                                            }));
                                        }
                                    }}
                                >
                                    Add Part
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className="mt-3">
                        {task?.subtasks && (
                            <div className="flex flex-col w-full py-2 justify-start">
                                <label className="w-1/3 pb-3">Subtasks</label>
                                {task?.subtasks.map((subtask: Task, index: number) => (
                                    <div key={index} className="mb-3 flex items-center gap-2">
                                        <Button
                                            variant="ghost"
                                            color="secondary"
                                            size="sm"
                                            className="p-0"
                                            onClick={() =>
                                                setTask((task: Task) => ({
                                                    ...task,
                                                    subtasks: task?.subtasks?.map(
                                                        (subtask: Task, idx: number) =>
                                                            idx === index
                                                                ? {
                                                                      ...subtask,
                                                                      is_complete:
                                                                          !subtask.is_complete,
                                                                  }
                                                                : subtask
                                                    ),
                                                }))
                                            }
                                        >
                                            {!subtask?.is_complete ? (
                                                <AppImages.iconBadgeUncheck className="h-6 w-6 text-bold text-secondary" />
                                            ) : (
                                                <AppImages.iconBadgeCheck className="h-6 w-6 text-bold text-success" />
                                            )}
                                        </Button>
                                        <input
                                            type="text"
                                            value={subtask.name}
                                            onChange={(e) => {
                                                setTask((task: Task) => ({
                                                    ...task,
                                                    subtasks: task?.subtasks?.map(
                                                        (subtask: Task, idx: number) =>
                                                            idx === index
                                                                ? {
                                                                      ...subtask,
                                                                      name: e.target.value,
                                                                  }
                                                                : subtask
                                                    ),
                                                }));
                                            }}
                                            onBlur={() => {
                                                // Save logic here, e.g., call an API to save the updated subtask
                                            }}
                                            className="subtasksinput border-secondary text-sm w-full rounded p-2"
                                        />
                                        <IconButton
                                            variant="ghost"
                                            onClick={() => {
                                                setTask((task: Task) => ({
                                                    ...task,
                                                    subtasks: (subtask: Task, idx: number) =>
                                                        idx === index
                                                            ? {
                                                                  ...subtask,
                                                                  is_removed: true,
                                                              }
                                                            : subtask,
                                                }));
                                            }}
                                        >
                                            <AppImages.iconTrash className="text-error w-5 h-5" />
                                        </IconButton>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="flex w-full justify-between">
                            <Button
                                size="sm"
                                color="primary"
                                className="!align-start"
                                onClick={() => {
                                    if (
                                        !task?.subtasks?.length ||
                                        task?.subtasks?.[task?.subtasks?.length - 1]["name"] != ""
                                    ) {
                                        setTask((task: Task) => ({
                                            ...task,
                                            subtasks: [...(task?.subtasks || []), { name: "" }],
                                        }));
                                    }
                                    setTimeout(() => {
                                        const lastSubtaskInput = document.querySelector(
                                            `input.subtasksinput[value=""]`
                                        ) as HTMLInputElement;
                                        if (lastSubtaskInput) {
                                            lastSubtaskInput.focus();
                                        }
                                    }, 0);
                                }}
                            >
                                Add subtask
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                color="secondary"
                                className="!align-start"
                                onClick={() => {
                                    onClose(task);
                                }}
                            >
                                Save
                            </Button>
                        </div>
                    </div>
                </div>
            </Card.Body>
        </Card>
    );
};